import { createContext, useContext, useMemo, useState, SetStateAction, Dispatch } from 'react';
import { useQueryData, useSubscribeData } from 'spectacle-ui-game-bridge-react';

export type PreloginContextType = {
  locale: string | null;
  localeError: Error | null;
  GetDownloadedSizeInBytes: number;
  GetTotalSizeToDownload: number;
  GetAverageDownloadSpeed: number;
  InstallingVersion: number;
  NowVersion: number;
  ShowUsingDevTip: boolean;
  isCDNIFinished: boolean;
  isMobileSharedAccountEnabled: boolean;
  CDNIPromptMessage: string | null;
  CDNIUnexpectedError: boolean | null;
  CDNIUnexpectedErrorMessage: string | null;
  CDNIUnexpectedErrorCode: number | null;
  CDNIPromptName: string | null;
  CDNIRecoverableErrorMessage: string | null;
  CDNIGoToAppStore: boolean | null;
  CDNIRestart: boolean | null;
  CDNICellularDownloadTip: boolean;
  CDNIShowDiskSpaceFailedPopup: boolean;
  CDNIStateString: string | null;
  CDNIStateEnum: number | null;
  CDNIPromptUseDevServer: boolean | null;
  diskFreeSize: number;
  device_os: string | null;
  autoLoginStatus: number | null;
  CDNIPromptErrorMessage: string | null;
  isUsingCachedLoginToken: boolean | null;
  build: string | null;
  CDNICellularDownloadTipClose: boolean | null;
  accountRegistrationStatus: string | null;
  hasValidSoftAccountUsername: boolean | null;
  hasValidToken: boolean | null;
  hasAnExistingSoftAccount: boolean | null;
  hasCreatedSoftAccount: boolean | null;
  hasUserAccountUpgraded: boolean | null;
  systemCountry: string | null;
  isSoftPromptShown: boolean | null;
  setIsSoftPromptShown: Dispatch<SetStateAction<boolean>>;
  isGuestLoginErrorShown: boolean;
  setIsGuestLoginErrorShown: Dispatch<SetStateAction<boolean>>;
  notificationDecision: boolean | null;
};

const PreloginContext = createContext<PreloginContextType>({
  locale: null,
  localeError: null,
  GetDownloadedSizeInBytes: 0,
  GetTotalSizeToDownload: 1,
  GetAverageDownloadSpeed: 0,
  InstallingVersion: 0,
  NowVersion: 0,
  ShowUsingDevTip: false,
  isCDNIFinished: false,
  isMobileSharedAccountEnabled: false,
  CDNIPromptMessage: null,
  CDNIUnexpectedError: null,
  CDNIUnexpectedErrorMessage: null,
  CDNIUnexpectedErrorCode: null,
  CDNIPromptName: null,
  CDNIRecoverableErrorMessage: null,
  CDNIGoToAppStore: null,
  CDNIRestart: null,
  CDNICellularDownloadTip: false,
  CDNIShowDiskSpaceFailedPopup: false,
  CDNIStateString: null,
  CDNIStateEnum: null,
  CDNIPromptUseDevServer: null,
  diskFreeSize: 0,
  device_os: null,
  autoLoginStatus: null,
  CDNIPromptErrorMessage: null,
  isUsingCachedLoginToken: null,
  build: null,
  CDNICellularDownloadTipClose: null,
  accountRegistrationStatus: null,
  hasValidSoftAccountUsername: null,
  hasValidToken: null,
  hasAnExistingSoftAccount: null,
  hasCreatedSoftAccount: null,
  hasUserAccountUpgraded: null,
  systemCountry: null,
  isSoftPromptShown: null,
  setIsSoftPromptShown: () => {},
  isGuestLoginErrorShown: false,
  setIsGuestLoginErrorShown: () => {},
  notificationDecision: false,
});

export const usePreloginContext = () => useContext(PreloginContext);

type PreloginContextProviderProps = React.PropsWithChildren<{}>;

export const PreloginContextProvider = ({ children }: PreloginContextProviderProps) => {
  const GetDownloadedSizeInBytes = useSubscribeData<number>('tos', 'GetDownloadedSizeInBytes') ?? 0;
  const GetTotalSizeToDownload = useSubscribeData<number>('tos', 'GetTotalSizeToDownload') ?? 1;
  const GetAverageDownloadSpeed = useSubscribeData<number>('tos', 'GetAverageDownloadSpeed') ?? 0;
  const InstallingVersion = useSubscribeData<number>('tos', 'InstallingVersion') ?? 0;
  const NowVersion = useSubscribeData<number>('tos', 'NowVersion') ?? 0;
  const ShowUsingDevTip = useSubscribeData<boolean>('tos', 'CDNIShowDevTip') ?? false;
  const isCDNIFinished = useSubscribeData<boolean>('tos', 'isCDNIFinished') ?? false;
  const isMobileSharedAccountEnabled = useSubscribeData<boolean>('tos', 'isMobileSharedAccountEnabled') ?? false;
  const CDNIPromptMessage = useSubscribeData<string>('tos', 'CDNIPromptMessage') ?? '';
  const CDNIUnexpectedError = useSubscribeData<boolean>('tos', 'CDNIUnexpectedError') ?? false;
  const CDNIUnexpectedErrorMessage = useSubscribeData<string>('tos', 'CDNIUnexpectedErrorMessage') ?? '';
  const CDNIUnexpectedErrorCode = useSubscribeData<number>('tos', 'CDNIUnexpectedErrorCode') ?? 0;
  const CDNIPromptName = useSubscribeData<string>('tos', 'CDNIPromptName') ?? '';
  const CDNIRecoverableErrorMessage = useSubscribeData<string>('tos', 'CDNIRecoverableErrorMessage') ?? '';
  const CDNIGoToAppStore = useSubscribeData<boolean>('tos', 'CDNIGoToAppStore') ?? false;
  const CDNIRestart = useSubscribeData<boolean>('tos', 'CDNIRestart') ?? false;
  const CDNICellularDownloadTip = useSubscribeData<boolean>('tos', 'CDNICellularDownloadTip') ?? false;
  const CDNIShowDiskSpaceFailedPopup = useSubscribeData<boolean>('tos', 'CDNIShowDiskSpaceFailedPopup') ?? false;
  const CDNIStateString = useSubscribeData<string>('tos', 'CDNIStateString');
  const CDNIStateEnum = useSubscribeData<number>('tos', 'CDNIStateEnum');
  const CDNIPromptUseDevServer = useSubscribeData<boolean>('tos', 'CDNIPromptUseDevServer') ?? false;
  const diskFreeSize = useSubscribeData<number>('tos', 'diskFreeSize') ?? 0;
  const device_os = useSubscribeData<string>('game', 'device_os') ?? '';
  const autoLoginStatus = useSubscribeData<number | null>('tos', 'autoLoginStatus');
  const CDNIPromptErrorMessage = useSubscribeData<string>('tos', 'CDNIPromptErrorMessage') ?? '';
  const CDNICellularDownloadTipClose = useSubscribeData<boolean>('tos', 'CDNICellularDownloadTipClose');
  const accountRegistrationStatus = useSubscribeData<string>('tos', 'AccountRegistrationStatus');
  const hasValidSoftAccountUsername = useSubscribeData<boolean>('tos', 'hasValidSoftAccountUsername');
  const hasValidToken = useSubscribeData<boolean>('tos', 'hasValidToken');
  const hasCreatedSoftAccount = useSubscribeData<boolean>('tos', 'hasCreatedSoftAccount');
  const hasUserAccountUpgraded = useSubscribeData<boolean>('tos', 'hasUserAccountUpgraded');
  const notificationDecision = useSubscribeData<boolean | null>('tos', 'notificationDecision');
  const [isUsingCachedLoginToken] = useQueryData<boolean>('tos', 'isUsingCachedLoginToken'); // will auto log in
  const [locale, localeError] = useQueryData<string>('game', 'locale');
  const [build] = useQueryData<string>('game', 'build_number');
  const [systemCountry] = useQueryData<string>('game', 'systemCountry');
  const [hasAnExistingSoftAccount] = useQueryData<boolean>('game', 'hasAnExistingSoftAccount');
  const [isSoftPromptShown, setIsSoftPromptShown] = useState(false);
  const [isGuestLoginErrorShown, setIsGuestLoginErrorShown] = useState(false);

  const state = useMemo<PreloginContextType>(
    () => ({
      locale,
      localeError,
      GetDownloadedSizeInBytes,
      GetTotalSizeToDownload,
      GetAverageDownloadSpeed,
      InstallingVersion,
      NowVersion,
      ShowUsingDevTip,
      isCDNIFinished,
      isMobileSharedAccountEnabled,
      CDNIPromptMessage,
      CDNIUnexpectedError,
      CDNIUnexpectedErrorMessage,
      CDNIUnexpectedErrorCode,
      CDNIPromptName,
      CDNIRecoverableErrorMessage,
      CDNIGoToAppStore,
      CDNIRestart,
      CDNICellularDownloadTip,
      CDNIShowDiskSpaceFailedPopup,
      CDNIStateString,
      CDNIStateEnum,
      CDNIPromptUseDevServer,
      diskFreeSize,
      device_os,
      autoLoginStatus,
      CDNIPromptErrorMessage,
      isUsingCachedLoginToken,
      build,
      CDNICellularDownloadTipClose,
      accountRegistrationStatus,
      hasValidSoftAccountUsername,
      hasValidToken,
      hasAnExistingSoftAccount,
      hasCreatedSoftAccount,
      hasUserAccountUpgraded,
      systemCountry,
      isSoftPromptShown,
      setIsSoftPromptShown,
      isGuestLoginErrorShown,
      setIsGuestLoginErrorShown,
      notificationDecision,
    }),
    [locale, localeError, GetDownloadedSizeInBytes, GetTotalSizeToDownload, GetAverageDownloadSpeed, InstallingVersion, NowVersion, ShowUsingDevTip, isCDNIFinished, isMobileSharedAccountEnabled, CDNIPromptMessage, CDNIUnexpectedError, CDNIUnexpectedErrorMessage, CDNIUnexpectedErrorCode, CDNIPromptName, CDNIRecoverableErrorMessage, CDNIGoToAppStore, CDNIRestart, CDNICellularDownloadTip, CDNIShowDiskSpaceFailedPopup, CDNIStateString, CDNIStateEnum, CDNIPromptUseDevServer, diskFreeSize, device_os, autoLoginStatus, CDNIPromptErrorMessage, isUsingCachedLoginToken, build, CDNICellularDownloadTipClose, accountRegistrationStatus, hasValidSoftAccountUsername, hasValidToken, hasAnExistingSoftAccount, hasCreatedSoftAccount, hasUserAccountUpgraded, systemCountry, isSoftPromptShown, setIsSoftPromptShown, isGuestLoginErrorShown, setIsGuestLoginErrorShown, notificationDecision]
  );

  return <PreloginContext.Provider value={state}>{children}</PreloginContext.Provider>;
};
